import { useState } from 'react';
import type { ListTasksData, TaskDto, TaskStatus } from '@/api/generated';
import {
  useDeleteTask,
  useProject,
  useSetTaskStatus,
  useSetTaskUrgent,
  useTasks,
} from '@/api/hooks';
import { QueryBoundary } from '@/api/QueryBoundary';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import { Icon, type IconName } from '@/design-system/icon/Icon';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Card } from '@/design-system/primitives/Card/Card';
import { ConfirmDialog } from '@/design-system/primitives/Dialog/ConfirmDialog';
import { Pill } from '@/design-system/primitives/Pill/Pill';
import { formatRelativeDeadline, toNumber } from '@/lib/format';
import { TaskCalendar } from './TaskCalendar';
import { groupTasks } from './taskGrouping';
import { TaskCreateDialog, TaskEditDialog } from './TaskDialogs';
import styles from './Tasks.module.css';

const PHASE_LABEL: Record<string, string> = {
  Voor: 'Voorber.',
  Ontw: 'Ontwerp',
  Verg: 'Vergunningen',
  Aanb: 'Aanbest.',
  Ruw: 'Ruwbouw',
  Wwd: 'W&W-d.',
  Tech: 'Technieken',
  Afw: 'Afwerking',
  Opl: 'Oplevering',
  Naz: 'Nazorg',
};

// 2026-05-17 — Wettelijke deadlines voor Vlaamse nieuwbouw / renovatie.
// All categories below are bound by Belgian/Flemish construction law and
// the dates are derived from the user's voorlopige-oplevering date
// (`project.endDate` is treated as that signal). When no end date is set
// yet the card is hidden so the user never sees invented dates.
//
// Sources audited 2026-05-17:
//   - EPB-aangifte: Vlaams Energieagentschap (VEKA), Energieprestatie-
//     regelgeving. Deadline = ingebruikname + 12 maanden. Indiening
//     door de verslaggever; overschrijden = administratieve geldboete.
//   - 10-jarige aansprakelijkheid: BW art. 1792 + art. 2270. Loopt
//     10 jaar vanaf de aanvaarding van het werk (= voorlopige oplevering).
//     Bouwheer kan in die periode schade aan dragende elementen claimen.
//   - Elektrische keuring: AREI (KB 08/09/2019) art. 6.4.1 — een
//     positief keuringsattest van een erkend organisme is verplicht
//     vóór de installatie in gebruik wordt genomen.
//   - Gaskeuring: KB 28/06/2019 — conformiteitsattest gas-installatie
//     vóór ingebruikname; verplicht aansluiten via een door Synergrid
//     erkend keuringsorganisme.
//   - Waterkeuring: VMM-regelgeving (Vlaamse Milieumaatschappij) —
//     keuring binneninstallatie water door de watermaatschappij vóór
//     definitieve aansluiting.
interface LegalDeadline {
  title: string;
  sub: string;
  date: string;
  icon: IconName;
}

function buildLegalDeadlines(endDateIso: string | null | undefined): LegalDeadline[] {
  if (!endDateIso) return [];
  const oplevering = new Date(endDateIso);
  if (Number.isNaN(oplevering.getTime())) return [];
  const fmt = (d: Date) => {
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${m}/${d.getFullYear()}`;
  };
  const offset = (months: number) => {
    const d = new Date(oplevering);
    d.setMonth(d.getMonth() + months);
    return d;
  };
  const addYears = (years: number) => {
    const d = new Date(oplevering);
    d.setFullYear(d.getFullYear() + years);
    return d;
  };
  return [
    // Pre-oplevering keuringen — must be in place BEFORE the building is
    // taken into use. Shown with the oplevering date as the deadline.
    {
      title: 'Elektrische keuring (AREI)',
      sub: 'verplicht vóór ingebruikname · door erkend organisme',
      date: fmt(oplevering),
      icon: 'tool',
    },
    {
      title: 'Gaskeuring (CERGA / Synergrid)',
      sub: 'verplicht vóór ingebruikname als gas-installatie aanwezig',
      date: fmt(oplevering),
      icon: 'tool',
    },
    {
      title: 'Waterkeuring binneninstallatie',
      sub: 'door de watermaatschappij · vóór aansluiting',
      date: fmt(oplevering),
      icon: 'tool',
    },
    // Start van de 10-jarige aansprakelijkheid.
    {
      title: '10-jarige aansprakelijkheid — start',
      sub: 'BW art. 1792 · loopt vanaf voorlopige oplevering',
      date: fmt(oplevering),
      icon: 'clock',
    },
    // Post-oplevering: EPB indienen.
    {
      title: 'EPB-aangifte indienen',
      sub: 'binnen 12m na ingebruikname · door verslaggever bij VEKA',
      date: fmt(offset(12)),
      icon: 'shield',
    },
    // Einde van de 10-jarige termijn — handig om in de gaten te houden.
    {
      title: '10-jarige aansprakelijkheid — einde',
      sub: 'na deze datum vervalt de wettelijke garantie op stevigheid',
      date: fmt(addYears(10)),
      icon: 'shield',
    },
  ];
}

const NEXT_STATUS: Record<TaskStatus, TaskStatus> = {
  Open: 'InProgress',
  InProgress: 'Done',
  Done: 'Open',
  // Blocked is a manual state; clicking the checkbox brings it back to Open.
  Blocked: 'Open',
};

const STATUS_LABEL: Record<TaskStatus, string> = {
  Open: 'open',
  InProgress: 'bezig',
  Done: 'afgerond',
  Blocked: 'geblokkeerd',
};

interface GroupProps {
  title: string;
  count: string;
  urgent?: boolean;
  children: React.ReactNode;
}

function Group({ title, count, urgent, children }: GroupProps) {
  return (
    <div>
      <div className={`row gap-8 ${styles.groupHead} ${urgent ? styles.groupHeadUrgent : ''}`}>
        <Icon name="chevD" size={12} color={urgent ? 'var(--accent-2)' : 'var(--ink-3)'} />
        <span className={`eyebrow ${urgent ? styles.groupTitleUrgent : ''}`}>{title}</span>
        <span className={`mono ${urgent ? styles.groupCountUrgent : styles.groupCount}`}>
          {count}
        </span>
      </div>
      {children}
    </div>
  );
}

function TaskRow({
  task,
  onEdit,
  onDelete,
}: {
  task: TaskDto;
  onEdit: (t: TaskDto) => void;
  onDelete: (t: TaskDto) => void;
}) {
  const setStatus = useSetTaskStatus();
  const setUrgent = useSetTaskUrgent();
  const [menuOpen, setMenuOpen] = useState(false);

  const blocked = task.status === 'Blocked';
  const done = task.status === 'Done';
  const inProgress = task.status === 'InProgress';

  return (
    <div className={`row gap-12 ${styles.taskRow}`}>
      <button
        type="button"
        role="checkbox"
        aria-checked={done}
        aria-label={`Status: ${STATUS_LABEL[task.status]} · klik om te wijzigen`}
        className={`${styles.taskBox} ${task.urgent ? styles.taskBoxUrgent : ''} ${
          blocked ? styles.taskBoxBlocked : ''
        } ${done ? styles.taskBoxDone : ''}`}
        onClick={() => setStatus.mutate({ id: task.id, status: NEXT_STATUS[task.status] })}
        disabled={setStatus.isPending}
      >
        {done && <Icon name="check" size={12} />}
        {inProgress && <span className={styles.taskBoxDot} aria-hidden="true" />}
      </button>
      <div className="flex-1">
        <div className="row gap-8">
          <span className={`${styles.taskTitle} ${done ? styles.taskTitleDone : ''}`}>
            {task.title}
          </span>
          <Pill kind="idle" dot={false}>
            {PHASE_LABEL[task.phaseId] ?? task.phaseId}
          </Pill>
          {inProgress && <Pill kind="warn">bezig</Pill>}
          {blocked && (
            <Pill kind="bad" dot={false}>
              geblokkeerd
            </Pill>
          )}
        </div>
        {task.note && <div className={styles.taskNote}>{task.note}</div>}
      </div>
      <div className={`row gap-8 ${styles.taskMeta}`}>
        <button
          type="button"
          className={`${styles.urgentBtn} ${task.urgent ? styles.urgentBtnOn : ''}`}
          aria-label={task.urgent ? 'Urgent-vlag uitzetten' : 'Markeer als urgent'}
          aria-pressed={task.urgent}
          onClick={() => setUrgent.mutate({ id: task.id, urgent: !task.urgent })}
          disabled={setUrgent.isPending}
        >
          <Icon name="flag" size={12} />
        </button>
        <Pill kind="idle" dot={false}>
          {task.assignedTo}
        </Pill>
        <span className={`mono ${styles.taskDue} ${task.urgent ? styles.taskDueUrgent : ''}`}>
          {formatRelativeDeadline(task.due)}
        </span>
        <div className={styles.kebabWrap}>
          <button
            type="button"
            className={styles.kebabBtn}
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Meer acties"
          >
            <Icon name="more" size={14} />
          </button>
          {menuOpen && (
            <>
              <button
                type="button"
                className={styles.kebabBackdrop}
                aria-label="Sluit menu"
                tabIndex={-1}
                onClick={() => setMenuOpen(false)}
              />
              <div role="menu" className={styles.kebabMenu}>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onEdit(task);
                  }}
                >
                  Bewerk
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    setStatus.mutate({
                      id: task.id,
                      status: blocked ? 'Open' : 'Blocked',
                    });
                  }}
                >
                  {blocked ? 'Deblokkeer' : 'Blokkeer'}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="c-bad"
                  onClick={() => {
                    setMenuOpen(false);
                    onDelete(task);
                  }}
                >
                  Verwijder
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

interface Filters {
  onlyOpen: boolean;
  onlyUrgent: boolean;
}

const DEFAULT_FILTERS: Filters = { onlyOpen: true, onlyUrgent: false };

export function Tasks() {
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [view, setView] = useState<'lijst' | 'kalender'>('lijst');
  // The list endpoint exposes status / phaseId / page / pageSize. Urgent is
  // not a server-side filter, so applied client-side below.
  const query: ListTasksData['query'] = {
    pageSize: 50,
    ...(filters.onlyOpen ? { status: 'Open' as const } : {}),
  };
  const tasks = useTasks(query);
  const project = useProject();
  const legalDeadlines = buildLegalDeadlines(project.data?.endDate);
  const defaultOffsets =
    project.data?.taskNotificationDefaultOffsetsDays?.map(Number) ?? [7, 1];
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<TaskDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TaskDto | null>(null);
  const deleteMutation = useDeleteTask();

  return (
    <AppFrame>
      <QueryBoundary query={tasks} loading="Taken laden…">
        {(page) => {
          const items = filters.onlyUrgent ? page.items.filter((t) => t.urgent) : page.items;
          const { thisWeek, twoWeeks, later, blocked, done } = groupTasks(items);
          const urgent = thisWeek; // "Vandaag & deze week" group

          return (
            <>
              <div className={`row ${styles.headerRow}`}>
                <div>
                  <div className="eyebrow">
                    Taken · {toNumber(page.total)} taken · {thisWeek.filter((t) => t.urgent).length} urgent
                  </div>
                  <h1 className={styles.title}>Taken &amp; deadlines</h1>
                </div>
                <div className={`ml-auto row gap-8 ${styles.headerActions}`}>
                  <div className={`row gap-4 ${styles.segment}`}>
                    <Btn
                      variant="ghost"
                      className={`${styles.segmentBtn} ${view === 'lijst' ? styles.segmentActive : ''}`}
                      onClick={() => setView('lijst')}
                    >
                      Lijst
                    </Btn>
                    <Btn
                      variant="ghost"
                      className={`${styles.segmentBtn} ${view === 'kalender' ? styles.segmentActive : ''}`}
                      onClick={() => setView('kalender')}
                    >
                      Kalender
                    </Btn>
                  </div>
                  <Btn variant="primary" onClick={() => setCreateOpen(true)}>
                    <Icon name="plus" size={14} /> Taak
                  </Btn>
                </div>
              </div>
              <TaskCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
              {editTarget && (
                <TaskEditDialog
                  open={editTarget !== null}
                  onOpenChange={(o) => {
                    if (!o) setEditTarget(null);
                  }}
                  task={editTarget}
                />
              )}
              <ConfirmDialog
                open={deleteTarget !== null}
                onOpenChange={(o) => {
                  if (!o) setDeleteTarget(null);
                }}
                title={`${deleteTarget?.title ?? 'Taak'} verwijderen?`}
                description="De taak wordt definitief verwijderd."
                confirmLabel="Verwijder"
                destructive
                pending={deleteMutation.isPending}
                onConfirm={async () => {
                  if (!deleteTarget) return;
                  try {
                    await deleteMutation.mutateAsync(deleteTarget.id);
                    setDeleteTarget(null);
                  } catch {
                    // toast surfaced by hook; keep dialog open so user can retry.
                  }
                }}
              />

              <div className={styles.body}>
                <Card className={styles.tasksCard}>
                  {view === 'kalender' ? (
                    <TaskCalendar tasks={items} onSelect={setEditTarget} />
                  ) : (
                    <>
                  {urgent.length > 0 && (
                    <Group title="Vandaag & deze week" count={String(urgent.length)} urgent>
                      {urgent.map((t) => (
                        <TaskRow
                          key={t.id}
                          task={t}
                          onEdit={setEditTarget}
                          onDelete={setDeleteTarget}
                        />
                      ))}
                    </Group>
                  )}
                  {twoWeeks.length > 0 && (
                    <Group title="Volgende 2 weken" count={String(twoWeeks.length)}>
                      {twoWeeks.map((t) => (
                        <TaskRow
                          key={t.id}
                          task={t}
                          onEdit={setEditTarget}
                          onDelete={setDeleteTarget}
                        />
                      ))}
                    </Group>
                  )}
                  {later.length > 0 && (
                    <Group title="Later" count={String(later.length)}>
                      {later.map((t) => (
                        <TaskRow
                          key={t.id}
                          task={t}
                          onEdit={setEditTarget}
                          onDelete={setDeleteTarget}
                        />
                      ))}
                    </Group>
                  )}
                  {blocked.length > 0 && (
                    <Group title="Geblokkeerd" count={String(blocked.length)}>
                      {blocked.map((t) => (
                        <TaskRow
                          key={t.id}
                          task={t}
                          onEdit={setEditTarget}
                          onDelete={setDeleteTarget}
                        />
                      ))}
                    </Group>
                  )}
                  {done.length > 0 && !filters.onlyOpen && (
                    <Group title="Afgerond" count={String(done.length)}>
                      {done.map((t) => (
                        <TaskRow
                          key={t.id}
                          task={t}
                          onEdit={setEditTarget}
                          onDelete={setDeleteTarget}
                        />
                      ))}
                    </Group>
                  )}
                  {items.length === 0 && (
                    <div className="mono c-ink-3" style={{ padding: '24px' }}>
                      Geen openstaande taken.
                    </div>
                  )}
                    </>
                  )}
                </Card>

                <div className="col gap-16">
                  <Card className={styles.legalCard}>
                    <div className="row gap-8">
                      <Icon name="shield" size={16} color="var(--bad)" />
                      <h3 className={styles.legalTitle}>Wettelijke deadlines</h3>
                    </div>
                    {legalDeadlines.length === 0 ? (
                      <p className={styles.legalSub}>
                        Stel een geplande oplevering-datum in via Instellingen → Project om je
                        wettelijke deadlines te zien.
                      </p>
                    ) : (
                      <>
                        <p className={styles.legalSub}>
                          Bewaakt door het platform — krijgt notificatie 2 maand én 2 weken op
                          voorhand.
                        </p>
                        <div className={`col gap-8 ${styles.legalList}`}>
                          {legalDeadlines.map((d) => (
                            <div key={d.title} className={`row gap-8 ${styles.legalRow}`}>
                              <Icon name={d.icon} size={14} color="var(--bad)" />
                              <div className="flex-1">
                                <div className={styles.legalRowTitle}>{d.title}</div>
                                <div className={styles.legalRowSub}>{d.sub}</div>
                              </div>
                              <span className={`mono ${styles.legalDate}`}>{d.date}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </Card>

                  <Card className={styles.filterCard}>
                    <h3>Filters</h3>
                    <div className={`col gap-6 ${styles.filterList}`}>
                      <label className={`row gap-6 ${styles.filterRow}`}>
                        <input
                          type="checkbox"
                          checked={filters.onlyOpen}
                          onChange={(e) =>
                            setFilters((f) => ({ ...f, onlyOpen: e.target.checked }))
                          }
                          className={styles.checkbox}
                        />
                        <span>Alleen open</span>
                      </label>
                      <label className={`row gap-6 ${styles.filterRow}`}>
                        <input
                          type="checkbox"
                          checked={filters.onlyUrgent}
                          onChange={(e) =>
                            setFilters((f) => ({ ...f, onlyUrgent: e.target.checked }))
                          }
                          className={styles.checkbox}
                        />
                        <span>Alleen urgent</span>
                      </label>
                    </div>
                  </Card>

                  <Card className={styles.notifCard}>
                    <h3 className={styles.notifTitle}>Notificaties</h3>
                    <div className={`col gap-6 ${styles.notifList}`}>
                      <div className="row gap-6">
                        <Icon name="mail" size={12} color="var(--ink-3)" />
                        E-mail · {defaultOffsets.join(' en ')} dagen voor deadline
                      </div>
                      <div className="row gap-6">
                        <Icon name="mail" size={12} color="var(--ink-3)" />
                        Wettelijke deadlines · 2 maanden en 2 weken vooraf
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </>
          );
        }}
      </QueryBoundary>
    </AppFrame>
  );
}
